// @ts-strict-ignore
import { CopiedRangeOnLineArticleInformation } from '../model/copied-range-on-line-article-information';
import { PasteArticlePresenter } from '../presenter/paste-article.presenter';
import { BuildingBlockVO } from '../../../model/vo/building-block.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleInfoVO } from '../../../model/vo/project.vo';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { CopiedArticleInformation } from '../model/copied-article-information';
import { ArticleUtils } from '@article/util/article-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Euler, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectModel } from '../../../model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Dictionary } from 'typescript-collections';
import { ProjectUtils } from '../../project/util/project-utils';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { Injectable } from '@angular/core';
import { ArticleByTypeManager } from '@article/manager/article-by-type.manager';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { BlockingZoneArticleManagerFactory } from './blocking-zone-article-manager.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { BuildingBlockPasteArticleManager } from './building-block-paste-article.manager';
import { PasteArticleManager } from './paste-article.manager';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ProjectDataService } from '../../../service/project-data.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticleService } from '@article/service/article.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';

@Injectable({ providedIn: 'root' })
export class BuildingBlockArticleManager {
  private cachedBuildingBlocksDictionary: Dictionary<string, BuildingBlockVO> = new Dictionary<string, BuildingBlockVO>();

  constructor(
    private insertArticleUtils: InsertArticleUtils,
    private alertHelper: AlertHelper,
    private projectDataService: ProjectDataService,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private articleManager: ArticleManager,
    private buildingBlockPasteArticleManager: BuildingBlockPasteArticleManager,
    private overlayService: OverlayService,
    private mountingRailHelper: MountingRailHelper,
    private pasteArticleManager: PasteArticleManager,
    private articleByTypeManager: ArticleByTypeManager,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private blockingZoneArticleManagerFactory: BlockingZoneArticleManagerFactory,
    private undoRedoUtils: UndoRedoUtils,
    private insertArticleService: InsertArticleService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private articleUsageService: ArticleUsageService,
    private articleService: ArticleService,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {}

  public async addBuildingBlockToProject(buildingBlockName: string, manualInsertionMode: boolean = true): Promise<void> {
    const cableFedSide: string = this.projectModel.currentInfoProperties.getString("buildingBlockCableFedSide");
    if (!cableFedSide || this.isOnlyRailInProject()) {
      const isOverlayShown: boolean = this.overlayService.getCurrentState();
      if (isOverlayShown) {
        this.overlayService.requestOverlayHide(OverlayBackground.color);
      }

      await this.projectComponentPresenter.openSelectCableFedSidePopup();
      if (isOverlayShown) {
        this.overlayService.requestOverlayShow(OverlayBackground.color);
      }
    }
    await this.startAddBuildingBlockToProject(buildingBlockName, manualInsertionMode);
  }

  private async startAddBuildingBlockToProject(buildingBlockName: string, manualInsertionMode: boolean): Promise<void> {
    const cachedBuildingBlock: BuildingBlockVO = this.getBuildingBlockFromCache(buildingBlockName);
    if (cachedBuildingBlock) {
      await this.continueAddBuildingBlock(cachedBuildingBlock, manualInsertionMode);
    } else {
      const buildingBlock = await this.projectDataService.loadBuildingBlockByName(buildingBlockName).toPromise()
      if (buildingBlock) {
        this.cachedBuildingBlocksDictionary.setValue(buildingBlockName, buildingBlock);
        await this.continueAddBuildingBlock(buildingBlock, manualInsertionMode);
      } else {
        if (manualInsertionMode) {
          this.alertHelper.showError('iekAssistant.title.building_block_not_found', 'iekAssistant.message.building_block_by_name_not_found', {translationParams: [buildingBlockName]});
        }
      }
    }
  }

  private async continueAddBuildingBlock(buildingBlock: BuildingBlockVO, manualInsertionMode: boolean): Promise<void> {
    const articles: ArticleVO[] = buildingBlock.complexProjectVO.articles;

    const dynamicArticleVOs: ArticleVO[] = buildingBlock.complexProjectVO.dynamicArticlesMap.values();

    const totalProjectLength: number = buildingBlock.complexProjectVO.project.infoProperties.getFloat("buildingBlockTotalLength");

    const startBuildingBlockPosition: number = buildingBlock.complexProjectVO.project.infoProperties.getFloat("buildingBlockStartPosition");
    console.log("BBA", totalProjectLength, startBuildingBlockPosition)
    const articleUsages = ProjectUtils.articleUsages(buildingBlock.complexProjectVO.project)
    await this.articleManager.addArticlesToCache(articles);

    const rangeOnLineArticleInformation: CopiedRangeOnLineArticleInformation = this.generateRangeOnlineArticleInformation(
      articleUsages,
      buildingBlock.complexProjectVO.project.dynamicArticlesInfo,
      dynamicArticleVOs,
      totalProjectLength,
      startBuildingBlockPosition
    );
    const pastePresenter: PasteArticlePresenter = this.getPasteArticlePresenter();
    this.buildingBlockPasteArticleManager.manualInsertionMode = manualInsertionMode;
    this.buildingBlockPasteArticleManager.setRangeOnLineArticleInformation(rangeOnLineArticleInformation);
    this.buildingBlockPasteArticleManager.setPastePresenter(pastePresenter);
    pastePresenter.isBuildingBlockPaste = true;
    pastePresenter.isPasteOperation = true;
    pastePresenter.displayErrors = manualInsertionMode;
    if (this.buildingBlockPasteArticleManager.isPasteOperationAvailable()) {
      await pastePresenter.executePasteOperation(PasteArticlePresenter.PASTE_AFTER_EACH_SELECTED_ARTICLE, 0, 1, true, 1);
    } else if (manualInsertionMode) {
      this.alertHelper.showError('global.title.error', 'Building block can not be connected to this project');
    }
  }

  private generateRangeOnlineArticleInformation(
    articleUsages: ArticleUsageVO[],
    dynamicArticlesInfo: ArticleInfoVO[],
    dynamicArticleVOs: ArticleVO[],
    totalProjectLength: number,
    startBuildingBlockPosition: number
  ): CopiedRangeOnLineArticleInformation {
    const rangeOnLineArticleInformation: CopiedRangeOnLineArticleInformation = new CopiedRangeOnLineArticleInformation();
    const insertArticleHelper: InsertArticleHelper = this.getInsertArticleHelper();
    const startPoint: Vector3 = insertArticleHelper ? insertArticleHelper.startPoint : new Vector3(0, 0, 0);
    rangeOnLineArticleInformation.overlapInfo = new OverlapInfo();
    rangeOnLineArticleInformation.projectType = this.projectModel.currentProject.projectType;
    rangeOnLineArticleInformation.rangeOnLineArticleType = ArticleTypeConstants.TYPE_MOUNTING_RAIL;
    let minPosX: number = Infinity;

    articleUsages
      .filter((u) => this.isPartOfBuildingBlock(u))
      .forEach((articleUsage: ArticleUsageVO) => {
        const copiedArticleInformation: CopiedArticleInformation = new CopiedArticleInformation();
        copiedArticleInformation.articleUsage = articleUsage;
        let articleVO: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
        articleVO = articleVO
          ? articleVO
          : dynamicArticleVOs.find((articleVO: ArticleVO) => {
              return articleVO.identity === articleUsage.articleId;
            });
        if (articleVO && ArticleUtils.isDynamicArticle(articleVO)) {
          // set params for dynamic article
          copiedArticleInformation.isDynamic = true;
          copiedArticleInformation.dynamicArticleMetaElements = [];
          articleVO.metaElements.forEach((el) => copiedArticleInformation.dynamicArticleMetaElements.push(CloneUtils.cloneMetaElementVO(el)));
          const dynamicInfo: ArticleInfoVO = ArticleUtils.findArticleInfoByGuidInSource(articleUsage.id, dynamicArticlesInfo);
          if (dynamicInfo) {
            copiedArticleInformation.dynamicArticleInfo = CloneUtils.cloneArticleInfoVO(dynamicInfo);
          }
        }
        const articleUsagePositionX: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
          articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X
        );
        const articleUsagePositionY: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
          articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y
        );
        const articleUsagePositionZ: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
          articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z
        );

        const articleUsageRotationX: number = MathUtils.toRadians(
          ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_X)
        );
        const articleUsageRotationY: number = MathUtils.toRadians(
          ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Y)
        );
        const articleUsageRotationZ: number = MathUtils.toRadians(
          ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Z)
        );

        copiedArticleInformation.rotation = new Euler(articleUsageRotationX, -articleUsageRotationY, -articleUsageRotationZ, 'ZYX');
        copiedArticleInformation.offsetFromCenter = new Vector3(-articleUsagePositionX, articleUsagePositionY, articleUsagePositionZ).sub(startPoint);

        rangeOnLineArticleInformation.articlesInformation.push(copiedArticleInformation);

        const backwardConnectedArticleUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
        const backwardConnectedArticleUsages: ArticleUsageVO[] = articleUsages.filter(
          (usage) => backwardConnectedArticleUsageIds.indexOf(usage.id) !== -1
        );
        if (
          ArrayUtils.isNotEmpty(backwardConnectedArticleUsages) &&
          backwardConnectedArticleUsages.some((articleUsage) => this.articleUsageService.isMountingRailArticleUsage(articleUsage))
        ) {
          rangeOnLineArticleInformation.articlesOnRailInformation.push(copiedArticleInformation);
          minPosX = Math.min(articleUsagePositionX, minPosX);
        }
      });

    const startArticlePosition: number = Math.abs(startBuildingBlockPosition) - Math.abs(minPosX);
    const startArticlePositionVector: Vector3 = new Vector3(startArticlePosition, 0, 0);

    rangeOnLineArticleInformation.articlesInformation.forEach((information: CopiedArticleInformation) => {
      return information.offsetFromCenter.sub(startArticlePositionVector);
    });

    rangeOnLineArticleInformation.commonArticlesLength = totalProjectLength;
    return rangeOnLineArticleInformation;
  }

  private getBuildingBlockFromCache(buildingBlockName: string): BuildingBlockVO {
    return this.cachedBuildingBlocksDictionary.getValue(buildingBlockName);
  }

  private getInsertArticleHelper(): InsertArticleHelper {
    const rangeOnLineArticleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsages(this.projectModel.currentProject).find(
      (articleUsage: ArticleUsageVO) =>
        this.articleService.isRangeOnLineArticleById(articleUsage.articleId, articleUsage.articleVersion)
    );
    const rangeOnLineArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(rangeOnLineArticleUsage.id);
    const insertArticleHelper: InsertArticleHelper = rangeOnLineArticleContainer
      ? this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(rangeOnLineArticleContainer)
      : null;
    return insertArticleHelper;
  }

  private isPartOfBuildingBlock(usage: ArticleUsageVO): boolean {
    return ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(usage, ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK);
  }

  private isOnlyRailInProject(): boolean {
    const articleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    for (const container of articleContainers) {
      if (
        !this.articleUsageService.isMountingRailArticleUsage(container.articleUsage) &&
        !ArticleUtils.isDrillingHole(container.article)
      ) {
        return false;
      }
    }
    return true;
  }

  private getPasteArticlePresenter(): PasteArticlePresenter {
    return new PasteArticlePresenter(
      this.insertArticleUtils,
      this.alertHelper,
      this.projectModel,
      this.projectArticleService,
      this.mountingRailHelper,
      this.pasteArticleManager,
      this.buildingBlockPasteArticleManager,
      this.articleByTypeManager,
      this.selectionModelService,
      this.undoRedoHelper,
      this.blockingZoneArticleManagerFactory,
      this.containerService,
      this.undoRedoUtils,
      this.insertArticleHelperFactory,
      this.insertArticleService,
      this.applicationRefreshService,
      this.detachArticleUsageService
    );
  }
}
